import type { ElementFileUploadEvent } from '@coorum-research/shared-common/types'
import type { SurveyAnswerRecord } from '@coorum-research/shared-common/types/answer.type'
import type { HttpCatchError } from '../apis/http'
import { atom, useSetAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useCallback } from 'react'
import { mediaFileApi } from '../apis/media-file.api'

type Params = {
  campaignId: string
  surveyId: string
  surveyConfigurationId: string
}

const currentSurveyDataAtom = atom<SurveyAnswerRecord | null>(null)
const currentMediaFilesAtom = atom<string[]>([])
const currentSurveyCompleteDisabledAtom = atom<boolean>(false)

export function useAnswerFlowCommon({ campaignId, surveyId, surveyConfigurationId }: Params) {
  // re-render防止
  const setCurrentSurveyData = useSetAtom(currentSurveyDataAtom)
  const setCurrentMediaFiles = useSetAtom(currentMediaFilesAtom)
  const setCurrentSurveyCompleteDisabled = useSetAtom(currentSurveyCompleteDisabledAtom)

  // reset global state when hook is called
  setCurrentMediaFiles([])
  setCurrentSurveyCompleteDisabled(false)

  // re-render防止のため、値はgetterで取得する。
  // おそらく、file uploadの時にnative device(ios, android)から戻る時にrenderが発生するとrerenderで不具合の発生を対応するためatomとgetterを使っている
  // native deviceの対応はatomを使わなくてもrefactoringの余地ありかも(file upload設問については必ずテストする）
  const getCurrentMediaFiles = useAtomCallback(
    useCallback((get: any) => {
      return get(currentMediaFilesAtom)
    }, []),
  )

  const getCurrentSurveyData = useAtomCallback(
    useCallback((get: any) => {
      return get(currentSurveyDataAtom)
    }, []),
  )

  const getCurrentSurveyCompleteDisabled = useAtomCallback(
    useCallback((get: any) => {
      return get(currentSurveyCompleteDisabledAtom)
    }, []),
  )

  const { uploadSurveyAnswerImage } = mediaFileApi()

  /**
   * 1質問の回答が完了した時
   * storeに保存し、サーバー保存に備える
   */
  const onCompleteQuestion = useCallback((answerValue: SurveyAnswerRecord) => {
    // merge current survey data with new answer
    setCurrentSurveyData((prev) => {
      const newValues = { ...prev, ...answerValue }
      // console.log('onCompleteQuestion', newValues)
      return newValues
    })
  }, [campaignId, surveyId, surveyConfigurationId])

  /**
   * file upload設問でfileがアップロードされた時
   */
  const onUploadFiles = useCallback(async (options: ElementFileUploadEvent) => {
    const firstFile = options.files[0]

    try {
      const uploadResult = await uploadSurveyAnswerImage(firstFile)
      const callbackData = {
        file: firstFile,
        content: uploadResult.imageUrl,
      }
      // update currentMediaFiles
      setCurrentMediaFiles(prev => [...prev, uploadResult.mediaUid])

      options.callback([callbackData])
    } catch (error: unknown) {
      if ((error as HttpCatchError).message) {
        const message = (error as HttpCatchError).message
        options.callback([], [message])
      } else {
        options.callback([], ['An error occurred during file upload.'])
      }
    }
  }, [])

  return {
    onCompleteQuestion,
    onUploadFiles,
    setCurrentSurveyData,
    getCurrentSurveyData,
    setCurrentMediaFiles,
    getCurrentMediaFiles,
    setCurrentSurveyCompleteDisabled,
    getCurrentSurveyCompleteDisabled,
  }
}
